import { useComponentKeyListener } from '@/hooks/useComponentKeyListener'
import { useCallback, useMemo } from 'react'

enum FocusMove {
  NEXT = 'next',
  PREVIOUS = 'previous',
  END = 'end',
  START = 'start',
}

export type UseTabKeyHandlerOrientation = 'horizontal' | 'vertical'
export interface UseTabsListKeyHandlerProps {
  value?: string
  onChange: (value: string) => void
  tabsRef: React.MutableRefObject<{ [key: string]: HTMLElement | null }>
  tabListRef: React.MutableRefObject<HTMLElement | null>
  orientation?: UseTabKeyHandlerOrientation
}

/**
 * Get the next index in the specified direction, skipping disabled tabs.
 */
const getNextIndex = (
  currentIndex: number,
  direction: number,
  values: string[],
  tabsRef: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
) => {
  let newIndex = currentIndex
  do {
    newIndex = (newIndex + direction + values.length) % values.length
  } while (tabsRef.current[values[newIndex]]?.hasAttribute('disabled'))
  return newIndex
}

/**
 * Custom hook to handle keyboard navigation for a tab list component.
 *
 * This hook ensures that the tab list is accessible by providing keyboard
 * navigation using arrow keys, Home, and End keys. It supports both horizontal
 * and vertical orientations.
 *
 * @link https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
export const useTabsListKeyHandler = ({
  value,
  onChange,
  tabsRef,
  tabListRef,
  orientation = 'horizontal',
}: UseTabsListKeyHandlerProps) => {
  const switchTab = useCallback(
    (move: FocusMove) => {
      if (!value) return
      const values = Object.keys(tabsRef.current)
      const currentIndex = values.indexOf(value)

      let newIndex = currentIndex
      switch (move) {
        case FocusMove.NEXT:
          newIndex = getNextIndex(currentIndex, 1, values, tabsRef)
          break
        case FocusMove.PREVIOUS:
          newIndex = getNextIndex(currentIndex, -1, values, tabsRef)
          break
        case FocusMove.END:
          newIndex = values.length - 1
          while (tabsRef.current[values[newIndex]]?.hasAttribute('disabled')) {
            newIndex--
            if (newIndex < 0) return
          }
          break
        case FocusMove.START:
          newIndex = 0
          while (tabsRef.current[values[newIndex]]?.hasAttribute('disabled')) {
            newIndex++
            if (newIndex >= values.length) return
          }
          break
      }

      onChange(values[newIndex])
      tabsRef.current[values[newIndex]]?.focus()
    },
    [value, onChange, tabsRef],
  )

  const keyActionMap = useMemo(() => {
    const map: { [key in KeyboardEvent['key']]?: () => void } = {
      Home: () => switchTab(FocusMove.START),
      End: () => switchTab(FocusMove.END),
    }

    if (orientation === 'vertical') {
      map.ArrowUp = () => switchTab(FocusMove.PREVIOUS)
      map.ArrowDown = () => switchTab(FocusMove.NEXT)
    } else {
      map.ArrowLeft = () => switchTab(FocusMove.PREVIOUS)
      map.ArrowRight = () => switchTab(FocusMove.NEXT)
    }

    return map
  }, [switchTab, orientation])

  useComponentKeyListener(tabListRef, keyActionMap)
}
